// src/components/ui/ConfirmDialog.tsx
// Reusable confirmation dialog to replace native window.confirm()

import { useState, useCallback, useId } from 'react';
import Modal from './Modal';
import Button from './Button';
import { AlertTriangle, Trash2, HelpCircle } from 'lucide-react';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary' | 'warning';
  /**
   * A refusal to show INSIDE the dialog, instead of closing as though the
   * action had succeeded.
   *
   * The dialog is where the user's attention already is. Closing it and
   * leaving nothing behind — which is what happens when a caller logs the
   * error and moves on — is indistinguishable from the action having worked,
   * and the row is still there to prove otherwise.
   */
  error?: string | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const variantConfig = {
  danger: {
    icon: Trash2,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonVariant: 'destructive' as const,
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    buttonVariant: 'primary' as const,
  },
  primary: {
    icon: HelpCircle,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonVariant: 'primary' as const,
  },
};

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  // The title and message name and describe the dialog for assistive tech.
  const titleId = useId();
  const messageId = useId();

  const config = variantConfig[variant];
  const Icon = config.icon;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="sm"
      showCloseButton={false}
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      labelledBy={titleId}
      describedBy={messageId}
    >
      <div className="text-center sm:text-left">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full ${config.iconBg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <h3 id={titleId} className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p id={messageId} className="text-sm text-gray-600">{message}</p>
            {error && (
              <p
                data-testid="confirm-dialog-error"
                role="alert"
                className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full sm:w-auto"
        >
          {cancelLabel}
        </Button>
        <Button
          variant={config.buttonVariant}
          onClick={handleConfirm}
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

// Hook for easier state management
interface UseConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary' | 'warning';
}

interface UseConfirmDialogReturn {
  isOpen: boolean;
  itemToDelete: string | null;
  openDialog: (itemId: string) => void;
  closeDialog: () => void;
  ConfirmDialogComponent: React.FC<{ onConfirm: () => void | Promise<void> }>;
}

export function useConfirmDialog(options: UseConfirmDialogOptions): UseConfirmDialogReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const openDialog = useCallback((itemId: string) => {
    setItemToDelete(itemId);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setItemToDelete(null);
  }, []);

  const ConfirmDialogComponent: React.FC<{ onConfirm: () => void | Promise<void> }> = useCallback(
    ({ onConfirm }) => (
      <ConfirmDialog
        isOpen={isOpen}
        title={options.title}
        message={options.message}
        confirmLabel={options.confirmLabel}
        cancelLabel={options.cancelLabel}
        variant={options.variant}
        onConfirm={async () => {
          await onConfirm();
          closeDialog();
        }}
        onCancel={closeDialog}
      />
    ),
    [isOpen, options, closeDialog]
  );

  return {
    isOpen,
    itemToDelete,
    openDialog,
    closeDialog,
    ConfirmDialogComponent,
  };
}

// Simpler stateful component for common delete patterns
interface DeleteConfirmDialogProps {
  isOpen: boolean;
  itemName?: string;
  itemType?: string;
  /** Shown in place of closing — see `ConfirmDialogProps.error`. */
  error?: string | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function DeleteConfirmDialog({
  isOpen,
  itemName,
  itemType = 'item',
  error = null,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={`Delete ${itemType}?`}
      message={
        itemName
          ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
          : `Are you sure you want to delete this ${itemType}? This action cannot be undone.`
      }
      confirmLabel="Delete"
      cancelLabel="Cancel"
      variant="danger"
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
